import { type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import { X as CloseIcon, Menu02 } from "@untitledui/icons";
import { Button as AriaButton, Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { MizbanfaLogo } from "../../../foundations/logo/mizbanfa-logo";
import { cx } from "../../../../utils/cx";

/**
 * Mobile top bar + navigation drawer.
 *
 * The drawer is controlled by its own state and opened only by the menu button. (It used to be an
 * Aria `DialogTrigger` wrapping the whole header, so any pressable inside — e.g. the notification
 * bell, itself a trigger — was wired to the wrong overlay.) Header actions now live outside any trigger.
 */
export const MobileNavigationHeader = ({
    children,
    logo,
    actions,
    activeUrl,
}: PropsWithChildren<{ logo?: ReactNode; actions?: ReactNode; /** Closes the drawer when the route changes. */ activeUrl?: string }>) => {
    const [isOpen, setOpen] = useState(false);

    useEffect(() => {
        setOpen(false);
    }, [activeUrl]);

    return (
        <>
            <header className="sticky top-[var(--app-top-offset,0px)] z-40 flex h-16 items-center justify-between gap-2 border-b border-secondary bg-primary/90 px-3 ps-4 backdrop-blur-md supports-[backdrop-filter]:bg-primary/80 lg:hidden">
                <div className="flex min-w-0 items-center">{logo ?? <MizbanfaLogo className="h-7" />}</div>

                <div className="flex shrink-0 items-center gap-1">
                    {actions}
                    <AriaButton
                        aria-label="باز کردن منو"
                        aria-expanded={isOpen}
                        aria-controls="mobile-nav-drawer"
                        onPress={() => setOpen(true)}
                        className="flex size-10 cursor-pointer items-center justify-center rounded-lg text-fg-secondary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:scale-95"
                    >
                        <Menu02 className="size-6" aria-hidden="true" />
                    </AriaButton>
                </div>
            </header>

            <AriaModalOverlay
                isOpen={isOpen}
                onOpenChange={setOpen}
                isDismissable
                className={({ isEntering, isExiting }) =>
                    cx(
                        "fixed inset-0 z-50 cursor-pointer bg-overlay/70 pe-16 backdrop-blur-sm lg:hidden",
                        isEntering && "duration-300 ease-out animate-in fade-in",
                        isExiting && "duration-200 ease-in animate-out fade-out",
                    )
                }
            >
                <AriaButton
                    aria-label="بستن منو"
                    onPress={() => setOpen(false)}
                    className="fixed top-3 end-3 flex size-10 cursor-pointer items-center justify-center rounded-lg text-white/80 outline-focus-ring hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                    <CloseIcon className="size-6" aria-hidden="true" />
                </AriaButton>

                <AriaModal
                    className={({ isEntering, isExiting }) =>
                        cx(
                            "h-dvh w-full max-w-80 cursor-auto shadow-xl will-change-transform",
                            isEntering && "duration-300 ease-out animate-in slide-in-from-right rtl:slide-in-from-right ltr:slide-in-from-left",
                            isExiting && "duration-200 ease-in animate-out slide-out-to-right rtl:slide-out-to-right ltr:slide-out-to-left",
                        )
                    }
                >
                    <AriaDialog id="mobile-nav-drawer" aria-label="منوی اصلی" className="h-dvh outline-hidden focus:outline-hidden">
                        {children}
                    </AriaDialog>
                </AriaModal>
            </AriaModalOverlay>
        </>
    );
};
